import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Public Routes
    {
      path: "/",
      name: "home",
      component: () => import("@/pages/index.vue"),
    },
    { path: "/how-it-works", name: "how-it-works", component: () => import("@/pages/how-it-works/index.vue") },
    {
      path: "/category/:id",
      name: "category.view",
      component: () => import("@/pages/category/[id].vue"),
    },
    {
      path: "/password/reset",
      name: "password.reset",
      component: () => import("@/pages/password/reset.vue"),
    },

    // Admin Section
    {
      path: "/admin",
      component: () => import("@/pages/admin.vue"),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: "",
          name: "admin",
          redirect: { name: "admin.quiz.list" },
        },
        {
          path: "users",
          name: "admin.users",
          children: [
            {
              path: "",
              name: "admin.users.list",
              component: () => import("@/pages/admin/users/index.vue"),
            },
            {
              path: ":id",
              children: [
                {
                  path: "",
                  name: "admin.users.summary",
                  component: () =>
                    import("@/pages/admin/users/[id]/index.vue"),
                },
                {
                  path: ":qid",
                  children: [
                    {
                      path: "",
                      name: "admin.users.summary.quiz",
                      component: () =>
                        import("@/pages/admin/users/[id]/[qid]/index.vue"),
                    },
                    {
                      path: ":aid",
                      name: "admin.users.summary.quiz.report",
                      component: () =>
                        import("@/pages/admin/users/[id]/[qid]/[aid].vue"),
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "quiz",
          name: "admin.quiz",
          children: [
            {
              path: "",
              name: "admin.quiz.list",
              component: () => import("@/pages/admin/quiz/index.vue"),
            },
            {
              path: ":id",
              name: "admin.quiz.edit",
              component: () => import("@/pages/admin/quiz/[id].vue"),
            },
          ],
        },
        {
          path: "category",
          name: "admin.category",
          children: [
            {
              path: "",
              name: "admin.category.list",
              component: () => import("@/pages/admin/category/index.vue"),
            },
          ],
        },
      ],
    },

    // Authenticated App Section
    {
      path: "/app",
      name: "app",
      component: () => import("@/pages/app.vue"),
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          redirect: { name: "app.summary" },
        },
        {
          path: "profile",
          name: "app.profile",
          component: () => import("@/pages/app/profile.vue"),
        },
        {
          path: "session",
          name: "app.session",
          component: () => import("@/pages/app/session.vue"),
        },
        {
          path: "summary",
          children: [
            {
              path: "",
              name: "app.summary",
              component: () => import("@/pages/app/summary/index.vue"),
            },
            {
              path: ":id",
              children: [
                {
                  path: "",
                  name: "app.summary.id",
                  component: () =>
                    import("@/pages/app/summary/[id]/index.vue"),
                },
                {
                  path: ":aid",
                  name: "app.summary.id.report",
                  component: () =>
                    import("@/pages/app/summary/[id]/[aid].vue"),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// Global route guard
router.beforeEach(async (to, _, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth) {
    let user = authStore.user;
    if (!user) {
      user = await authStore.reAuth();
    }

    if (!user) {
      return next({ name: "home" });
    }

    if (to.meta.requiresAdmin && user.role !== "admin") {
      return next({ name: "home" });
    }
  }

  next();
});

export default router;
